/**
 * Google Campaign Types from Google Ads API enum codes.
 */
export function getGoogleCampaignType(channelType: number | string | undefined): string {
  const code = Number(channelType);
  if (code === 10) return "PERFORMANCE_MAX";
  if (code === 14) return "DEMAND_GEN";
  if (code === 2) return "SEARCH";
  if (code === 3) return "DISPLAY";
  if (code === 4) return "SHOPPING";
  if (code === 6) return "VIDEO";
  if (code === 5) return "HOTEL";
  if (code === 7) return "MULTI_CHANNEL";
  if (code === 8) return "LOCAL";
  if (code === 9) return "SMART";
  if (code === 11) return "LOCAL_SERVICES";
  if (code === 13) return "TRAVEL";
  return "UNKNOWN";
}

export interface UtmValidationResult {
  readonly rawUtm: string;
  readonly hasIssue: boolean;
  readonly issues: readonly string[];
  readonly fix: string;
}

export interface AdRecordForValidation {
  readonly raw_url?: string;
  readonly url_tags?: string;
  readonly utm?: string;
  readonly raw_url_parameters?: string;
  readonly url_parameters?: string;
  readonly advertising_channel_type?: number | string;
  readonly adv_channel_type?: number | string;
  readonly campaign_type?: string;
  readonly utm_source?: string;
  readonly utm_medium?: string;
  readonly utm_campaign?: string;
  readonly utm_content?: string;
  readonly utm_adset_name?: string;
  readonly ea_ad?: string;
}

const GOOGLE_AD_MACROS: Readonly<Record<string, { readonly macro: string; readonly label: string }>> = {
  PERFORMANCE_MAX: { macro: "{assetgroupid}", label: "Performance Max" },
  SHOPPING: { macro: "{product_id}", label: "Shopping" },
  SEARCH: { macro: "{creative}", label: "Search" },
  DEMAND_GEN: { macro: "{creative}", label: "Demand Gen" },
  DISPLAY: { macro: "{creative}", label: "Display" },
  VIDEO: { macro: "{creative}", label: "Video" },
  APP: { macro: "{creative}", label: "App" },
  MULTI_CHANNEL: { macro: "{creative}", label: "Multi-Channel / App" },
  LOCAL: { macro: "{creative}", label: "Local" },
  SMART: { macro: "{creative}", label: "Smart" },
  LOCAL_SERVICES: { macro: "{creative}", label: "Local Services" },
  HOTEL: { macro: "{creative}", label: "Hotel" },
  TRAVEL: { macro: "{creative}", label: "Travel" },
};

/**
 * Validates a UTM / URL parameter string against Meta & Google Ads Manager rules.
 */
export function validateUtmParameters(
  record: AdRecordForValidation = {},
  channel: "meta" | "google" = "meta",
): UtmValidationResult {
  const issues: string[] = [];

  let utmString =
    record.raw_url ||
    record.url_tags ||
    record.utm ||
    record.raw_url_parameters ||
    record.url_parameters ||
    "";

  if (!utmString && (record.utm_source || record.utm_medium || record.ea_ad)) {
    const parts: string[] = [];
    if (record.utm_source) parts.push(`utm_source=${record.utm_source}`);
    if (record.utm_medium) parts.push(`utm_medium=${record.utm_medium}`);
    if (record.utm_campaign) parts.push(`utm_campaign=${record.utm_campaign}`);
    if (record.utm_content) parts.push(`utm_content=${record.utm_content}`);
    if (record.utm_adset_name) parts.push(`Campaign+AdSetName=${record.utm_adset_name}`);
    if (record.ea_ad) parts.push(`ea_ad=${record.ea_ad}`);
    utmString = parts.join("&");
  }

  const ch = (channel || "meta").toLowerCase();

  let rawForParsing = utmString || "";
  rawForParsing = rawForParsing.replace(/\{lpurl\}|\{unescapedlpurl\}|\{escapedlpurl\}/gi, "");
  rawForParsing = rawForParsing.replace(/\?/g, "&");
  if (rawForParsing.startsWith("&")) rawForParsing = rawForParsing.slice(1);

  /**
   * `decodeURIComponent` throws `URIError` on a stray `%` (a real macro string can carry
   * one). The defined fallback is to use the raw, undecoded text for that pair — the
   * checker only looks at parameter NAMES and macro shapes, neither of which the decode
   * changes, so a failed decode never changes a verdict.
   */
  const safeDecode = (value: string): string => {
    try {
      return decodeURIComponent(value);
    } catch (caught) {
      void caught;
      return value;
    }
  };

  const paramsMap = new Map<string, string>();
  if (rawForParsing.trim()) {
    for (const pair of rawForParsing.split("&")) {
      if (!pair) continue;
      const eqIdx = pair.indexOf("=");
      if (eqIdx !== -1) {
        paramsMap.set(safeDecode(pair.slice(0, eqIdx)).trim().toLowerCase(), safeDecode(pair.slice(eqIdx + 1)).trim());
      } else {
        paramsMap.set(safeDecode(pair).trim().toLowerCase(), "");
      }
    }
  }

  const getVal = (aliases: readonly string[]): { key: string; value: string } | null => {
    for (const a of aliases) {
      const lower = a.toLowerCase();
      if (paramsMap.has(lower)) {
        return { key: lower, value: paramsMap.get(lower) ?? "" };
      }
    }
    return null;
  };

  if (ch === "google") {
    const channelTypeCode = Number(record.advertising_channel_type || record.adv_channel_type || 0);
    const campaignTypeStr = (record.campaign_type || "").toUpperCase().trim();

    let campaignType = channelTypeCode ? getGoogleCampaignType(channelTypeCode) : campaignTypeStr;
    if (!campaignType || campaignType === "UNKNOWN") {
      campaignType = campaignTypeStr || "UNKNOWN";
    }

    const adMacroInfo = GOOGLE_AD_MACROS[campaignType] ?? null;
    const adMacro = adMacroInfo ? adMacroInfo.macro : "{creative}";

    const sourceObj = getVal(["utm_source"]);
    if (!sourceObj || !sourceObj.value) {
      issues.push("Missing mandatory parameter 'utm_source=google'");
    } else if (sourceObj.value.toLowerCase() !== "google") {
      issues.push(`utm_source '${sourceObj.value}' should be 'google'`);
    }

    const mediumObj = getVal(["utm_medium"]);
    if (!mediumObj || !mediumObj.value) {
      issues.push("Missing mandatory parameter 'utm_medium' (expected: cpc)");
    } else if (mediumObj.value.toLowerCase() !== "cpc") {
      issues.push(`utm_medium '${mediumObj.value}' should be 'cpc'`);
    }

    const camObj = getVal(["ea_cam", "utm_campaign_id"]);
    if (!camObj || !camObj.value) {
      issues.push("Missing mandatory parameter 'ea_cam={campaignid}'");
    } else {
      const val = camObj.value;
      if (val.includes("[[") || val.includes("]]") || (val.includes("[") && !val.includes("{")) || val.includes("]")) {
        issues.push(`Invalid syntax in ea_cam '${val}': used square brackets instead of curly braces {campaignid}`);
      }
    }

    const adIdObj = getVal(["ea_ad"]);
    if (!adIdObj || !adIdObj.value) {
      issues.push(`Missing mandatory parameter 'ea_ad=${adMacro}'`);
    } else {
      const val = adIdObj.value;
      if (val.includes("[[") || val.includes("]]") || (val.includes("[") && !val.includes("{")) || val.includes("]")) {
        issues.push(`Invalid syntax in ea_ad '${val}': used square brackets instead of curly braces`);
      }
    }

    const recommendedFix = [
      "utm_source=google",
      "utm_medium=cpc",
      "ea_cam={campaignid}",
      "ea_grp={adgroupid}",
      `ea_ad=${adMacro}`,
      "ea_kw={keyword}",
      "ea_net=adwords",
      "placement={placement}",
    ].join("&");

    return {
      rawUtm: utmString || "No UTM parameters configured",
      hasIssue: issues.length > 0,
      issues,
      fix: recommendedFix,
    };
  }

  // META ADS RULES
  const sourceObj = getVal(["utm_source"]);
  if (!sourceObj || !sourceObj.value) {
    issues.push("Missing mandatory parameter 'utm_source=meta'");
  } else {
    const val = sourceObj.value;
    const validSources = ["meta", "facebook", "fb", "instagram", "ig"];
    if (!validSources.includes(val.toLowerCase())) {
      issues.push(`utm_source '${val}' is not valid — expected: meta`);
    }
  }

  const mediumObj = getVal(["utm_medium"]);
  if (!mediumObj || !mediumObj.value) {
    issues.push("Missing mandatory parameter 'utm_medium' (expected: {{placement}})");
  } else {
    const val = mediumObj.value;
    if (val.includes("[[") || val.includes("]]") || (val.includes("[") && !val.includes("{{")) || val.includes("]")) {
      issues.push(`Invalid syntax in utm_medium '${val}': used square brackets instead of double curly braces {{placement}}`);
    } else if (val.includes("placement") && val !== "{{placement}}") {
      issues.push(`Invalid syntax in utm_medium '${val}': must be {{placement}} with double curly braces`);
    } else if (val !== "{{placement}}") {
      issues.push(`utm_medium is set to '${val}' instead of placement tag {{placement}}`);
    }
  }

  const adIdObj = getVal(["ea_ad"]);
  if (!adIdObj || !adIdObj.value) {
    issues.push("Missing mandatory parameter 'ea_ad={{ad.id}}'");
  } else {
    const val = adIdObj.value;
    if (val.includes("[[") || val.includes("]]") || val.includes("[") || val.includes("]")) {
      issues.push(`Invalid syntax in ea_ad '${val}': used square brackets instead of double curly braces {{ad.id}}`);
    } else if (val.toLowerCase().includes("adid") || val.toLowerCase().includes("ad_id") || (val.includes("ad.id") && val !== "{{ad.id}}")) {
      issues.push(`Invalid tag format in ea_ad '${val}': must be {{ad.id}} with double curly braces`);
    }
  }

  const campaignObj = getVal(["utm_campaign", "campaign", "campaign name"]);
  if (campaignObj?.value) {
    const val = campaignObj.value;
    if (val.includes("[[") || val.includes("]]") || (val.includes("[") && !val.includes("{{")) || val.includes("]")) {
      issues.push(`Invalid syntax in utm_campaign '${val}': used square brackets instead of double curly braces {{campaign.name}}`);
    } else if (val === "{{adset.name}}" || val === "{{ad.name}}" || val === "{{placement}}") {
      issues.push(`utm_campaign is set to '${val}' — expected campaign tag {{campaign.name}}`);
    } else if (val.includes("campaign.name") && val !== "{{campaign.name}}") {
      issues.push(`Invalid syntax in utm_campaign '${val}': must be {{campaign.name}} with double curly braces`);
    }
  }

  const contentObj = getVal(["utm_content", "content", "campaign content"]);
  if (contentObj?.value) {
    const val = contentObj.value;
    if (val.includes("[[") || val.includes("]]") || (val.includes("[") && !val.includes("{{")) || val.includes("]")) {
      issues.push(`Invalid syntax in utm_content '${val}': used square brackets instead of double curly braces {{ad.name}}`);
    } else if (val === "{{campaign.name}}" || val === "{{adset.name}}" || val === "{{placement}}") {
      issues.push(`utm_content is set to '${val}' — expected ad tag {{ad.name}}`);
    } else if (val.includes("ad.name") && val !== "{{ad.name}}") {
      issues.push(`Invalid syntax in utm_content '${val}': must be {{ad.name}} with double curly braces`);
    }
  }

  const adsetObj = getVal(["campaign adsetname", "utm_adset_name", "adset_name"]);
  if (adsetObj?.value) {
    const val = adsetObj.value;
    if (val.includes("[[") || val.includes("]]") || (val.includes("[") && !val.includes("{{")) || val.includes("]")) {
      issues.push(`Invalid syntax in Campaign AdSetName '${val}': used square brackets instead of double curly braces {{adset.name}}`);
    } else if (val === "{{campaign.name}}" || val === "{{ad.name}}" || val === "{{placement}}") {
      issues.push(`Campaign AdSetName is set to '${val}' — expected adset tag {{adset.name}}`);
    } else if (val.includes("adset.name") && val !== "{{adset.name}}") {
      issues.push(`Invalid syntax in Campaign AdSetName '${val}': must be {{adset.name}} with double curly braces`);
    }
  }

  const recommendedFix = `utm_source=meta&utm_medium={{placement}}&utm_campaign={{campaign.name}}&utm_content={{ad.name}}&Campaign+AdSetName={{adset.name}}&ea_cam={{campaign.id}}&ea_grp={{adset.id}}&ea_ad={{ad.id}}`;

  return {
    rawUtm: utmString || "No UTM parameters configured",
    hasIssue: issues.length > 0,
    issues,
    fix: recommendedFix,
  };
}
